import { fetchWithTimeout } from '@noths/polaris-client-utils';

import { API_TIMEOUT } from 'src/settings';
import { authenticatedApiCall } from 'src/utils/apiCall/apiCall';
import { logError } from 'src/utils/logError';

const GENERIC_ERROR_MESSAGE = 'An error occurred while uploading your document';

export enum S3ResponseType {
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

interface S3ErrorResponse {
  error: string;
  type: S3ResponseType.ERROR;
}

interface S3SuccessResponse {
  type: S3ResponseType.SUCCESS;
  uploadReference: string;
}

export type S3Response = S3SuccessResponse | S3ErrorResponse;

interface UploadLocationResponse {
  uploadLocation: string;
  uploadReference: string;
}

const requestPresignedPostURL = async (mimeType: string) =>
  authenticatedApiCall<UploadLocationResponse>({
    url: '/upload-locations',
    method: 'POST',
    data: { mimeType },
  });

export const uploadToS3 = async (mimeType: string, fileContents: File): Promise<S3Response> => {
  try {
    const resp = await requestPresignedPostURL(mimeType);
    const { uploadLocation, uploadReference } = resp.data;

    const s3response = await fetchWithTimeout(uploadLocation, {
      method: 'PUT',
      headers: {
        'Content-Type': mimeType,
        'x-amz-server-side-encryption': 'AES256',
        'x-amz-tagging': 'expires=true',
      },
      body: fileContents,
      timeout: API_TIMEOUT,
    });

    if (s3response.status === 200) {
      return {
        type: S3ResponseType.SUCCESS,
        uploadReference,
      };
    }

    logError(`S3 file upload error response ${s3response.status}`);
    return {
      type: S3ResponseType.ERROR,
      error: GENERIC_ERROR_MESSAGE,
    };
  } catch (ex) {
    logError(`S3 presigned post url negotiation error: ${(ex as Error).message}`);
    return {
      type: S3ResponseType.ERROR,
      error: GENERIC_ERROR_MESSAGE,
    };
  }
};
